import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { segurosApi } from '../../../lib/api';
import { fmtFecha } from '../../../lib/utils';

const fmtCLP = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString('es-CL')}`);
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export function CalendarioCuotas({ onVerPoliza }) {
  const hoy = new Date();
  const [ym, setYm] = useState({ y: hoy.getFullYear(), m: hoy.getMonth() + 1 });
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await segurosApi.calendario(ym.y, ym.m);
      setData(res.data);
    } catch { setData(null); }
  }, [ym]);

  useEffect(() => { load(); }, [load]);

  const mover = (d) => setYm(({ y, m }) => {
    const nm = m + d;
    return { y: y + Math.floor((nm - 1) / 12), m: (nm - 1 + 12) % 12 + 1 };
  });

  return (
    <Card data-testid="calendario-cuotas">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm flex items-center gap-2">
          <CalendarDays className="w-4 h-4" /> Cuotas de {MESES[ym.m - 1]} {ym.y}
        </CardTitle>
        <div className="flex items-center gap-2">
          {data && (
            <span className="text-xs text-muted-foreground">
              {Number(data.total_uf).toLocaleString('es-CL', { maximumFractionDigits: 4 })} UF · <b className="text-foreground">{fmtCLP(data.total_clp)}</b>
            </span>
          )}
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => mover(-1)} data-testid="btn-mes-anterior"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => mover(1)} data-testid="btn-mes-siguiente"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {(!data || data.cuotas.length === 0) ? (
          <div className="text-xs text-muted-foreground py-3 text-center">Sin cuotas este mes</div>
        ) : (
          <div className="space-y-1">
            {data.cuotas.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-xs border rounded px-2 py-1.5 hover:bg-muted/40 cursor-pointer"
                onClick={() => onVerPoliza?.(c.poliza_id)} data-testid={`calendario-cuota-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono font-medium whitespace-nowrap">{fmtFecha(c.fecha_vencimiento)}</span>
                  <span className="truncate">{c.aseguradora} · Póliza {c.numero_poliza} · {c.bien_asegurado}</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <span>{Number(c.uf).toLocaleString('es-CL', { maximumFractionDigits: 4 })} UF</span>
                  <b>{fmtCLP(c.clp)}{c.clp_provisorio ? '~' : ''}</b>
                  <Badge variant={c.estado === 'pagada' ? 'default' : 'outline'} className={`text-[9px] ${c.estado === 'pagada' ? 'bg-emerald-600' : ''}`}>
                    {c.estado}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
